import config from '../../config/config';
import DefaultApiLimit from '../../config/defaultLimit';

import deleteRequest from './base/delete';
import getRequest from './base/get';
import patchRequest from './base/patch';
import postRequest from './base/post';

interface QueryParameters {
  limit?: number;
  [key: string]: any;
}

export default {
  /**
   * Fetch devices API
   * @param {any} id - Charging station ID
   * @returns {any} Promise containing device list
   */
  fetchDevices: (id: string) => {
    return getRequest(`${config.cpo.commonApiUrl}api/common/v1/vlbc/charging-points/${id}`);
  },

  /**
   * Fetch charging stations API
   * @returns {any} Promise containing charging stations list
   */
  fetchChargingStations: () => {
    return getRequest(`${config.cpo.commonApiUrl}api/common/v1/vlbc/charging-stations-list`);
  },

  /**
   * Create the load balancer
   * @param {any} data - Load balancer payload
   * @returns {any} Promise containing created VLBC response
   */
  createVlbc: (data: any): Promise<any> => {
    const parameters = { ...data };
    return postRequest(`${config.cpo.commonApiUrl}api/common/v1/vlbc`, {
      ...parameters,
    });
  },

  /**
   * Fetch vlbcs API
   * @param {any} query - Query parameters for VLBC list
   * @returns {any} Promise containing VLBC list
   */
  fetchVlbcs: (query: QueryParameters = {}) => {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(`${config.cpo.commonApiUrl}api/common/v1/vlbc`, {
      ...parameters,
    });
  },

  /**
   * Fetch vlbc API
   * @param {any} vlbcId - VLBC ID
   * @returns {any} Promise containing VLBC details
   */
  fetchVlbc: (vlbcId: string) => {
    return getRequest(`${config.cpo.commonApiUrl}api/common/v1/vlbc/${vlbcId}`);
  },

  /**
   * Update the load balancer
   * @param {any} vlbcId - VLBC ID
   * @param {any} data - Updated VLBC payload
   * @returns {any} Promise containing updated VLBC response
   */
  updateVlbc: (vlbcId: string, data: any): Promise<any> => {
    const parameters = { ...data };
    return patchRequest(`${config.cpo.commonApiUrl}api/common/v1/vlbc/${vlbcId}`, {
      ...parameters,
    });
  },

  /**
   * Update the load balancer status
   * @param {any} vlbcId - VLBC ID
   * @param {any} data - Status update payload
   * @returns {any} Promise containing updated status response
   */
  updateVlbcStatus: (vlbcId: string, data: any): Promise<any> => {
    const parameters = { ...data };
    return patchRequest(`${config.cpo.commonApiUrl}api/common/v1/vlbc/update-status/${vlbcId}`, {
      ...parameters,
    });
  },

  /**
   * Assign chargers count
   * @returns {any} Promise containing assigned chargers count
   */
  assignChargersCount: (): Promise<any> => {
    return getRequest(`${config.cpo.commonApiUrl}api/common/v1/vlbc/charger-assign-counts`);
  },

  /**
   * Delete vlbc
   * @param {any} vlbcId - VLBC ID
   * @returns {any} Promise containing delete response
   */
  deleteVlbc: (vlbcId: string): Promise<any> => {
    return deleteRequest(`${config.cpo.commonApiUrl}api/common/v1/vlbc/${vlbcId}`);
  },

  /**
   * Fetch vlbc logs
   * @param {any} vlbcId - VLBC ID
   * @param {any} query - Query parameters for logs
   * @returns {any} Promise containing VLBC logs
   */
  fetchVlbcLogs: (vlbcId: string, query: QueryParameters = {}) => {
    const parameters = { ...query };

    return getRequest(`${config.cpo.commonApiUrl}api/common/v1/vlbc/vlbc-logs/${vlbcId}`, {
      ...parameters,
    });
  },

  /**
   * Fetch vlbc overview
   * @param {any} vlbcId - VLBC ID
   * @returns {any} Promise containing VLBC overview
   */
  fetchVlbcOverview: (vlbcId: string) => {
    return getRequest(`${config.cpo.commonApiUrl}api/common/v1/vlbc/vlbc-overview/${vlbcId}`);
  },
};
